import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import queryString from "query-string";
import { FaSort } from "react-icons/fa";
import { Link } from "react-router-dom";
import sweetAlert from "sweetalert";

import {
  Breadcrumbs,
  Button,
  Loading,
  Page,
  Pagination,
  Panel,
} from "components";
import { inhabitantsAPI } from "services/inhabitantsAPI";

import { Filters } from "./components";

import { Access } from "common";

const CASHBACK_IMPORT_STATUS = {
  IMPORTED: "Importado",
  ACCOUNTED: "Contabilizado",
};

const breadcrumbs = [
  { name: "Home", to: "/" },
  { name: "PRO", to: "/pro" },
  { name: "Listagem de Cashbacks", to: "" },
];

const initialState = {
  cpf: "",
  start_at: null,
  end_at: null,
  reference_date: null,
  status: null,
  accessorName: "",
};

const numberFields = ["amount"];
const dateFields = ["created_at", "reference_date"];
const stringFields = [
  "customer_name",
  "customer_cpf",
  "status",
  "accessor_name",
];

const columnsSortInitialState = {
  customer_name: false,
  customer_cpf: false,
  accessor_name: false,
  amount: false,
  status: false,
  reference_date: false,
  created_at: false,
};

const strategiesMap = {
  stringStrategy: (a, b, column) => a[column].localeCompare(b[column]),
  numberStrategy: (a, b, column) => a[column] - b[column],
  dateStrategy: (a, b, column) =>
    new Date(a[column]).getTime() - new Date(b[column]).getTime(),
  unknownStrategy: () => 0,
};

export function List(props) {
  const [loading, setLoading] = useState(true);
  const [cashbacks, setCashbacks] = useState([]);
  const [totalCashbacks, setTotalCashbacks] = useState(0);
  const [pagination, setPagination] = useState(getCurrentPagination());
  const [filters, setFilters] = useState(initialState);
  const [isColumnSorted, setIsColumnSorted] = useState(columnsSortInitialState);
  const debounceTimer = useRef(null);

  const parseCashbackImportStatus = (status = "") =>
    CASHBACK_IMPORT_STATUS[status.toUpperCase()] || "Desconhecida";

  const formatCpf = (cpf) =>
    cpf.match(/\d/g) ? cpf.match(/\d/g).join("") : "";

  const isCpfValid = (cpf) => cpf && formatCpf(cpf).length === 11;

  const formatReferenceDate = (date) =>
    date ? moment(date).format("YYYY-MM-DD") : null;

  function fetchData() {
    setLoading(true);

    const options = {
      params: {
        per: pagination.per,
        page: pagination.page,
        start_at: filters.start_at,
        end_at: filters.end_at,
        cpf: formatCpf(filters.cpf) || null,
        reference: formatReferenceDate(filters.reference_date),
        status: filters.status && filters.status.value,
        accessorName: filters.accessorName,
      },
    };

    inhabitantsAPI
      .get("cashbacks", options)
      .then(({ data }) => {
        if (!data || !data.data) {
          return;
        }

        if (Array.isArray(data.data)) {
          setCashbacks(
            data.data.map(({ attributes }) => ({
              id: attributes.id,
              accessor_name: attributes.accessor && attributes.accessor.name,
              customer_name: attributes.customer && attributes.customer.name,
              customer_cpf: attributes.customer && attributes.customer.cpf,
              amount: attributes.amount,
              status: attributes.status,
              reference_date: attributes.reference_date,
              created_at: attributes.created_at,
            }))
          );
        }

        if (data.meta && data.meta.total_count) {
          setTotalCashbacks(data.meta.total_count);
        }
      })
      .catch((error) => {
        sweetAlert(
          "Algo deu errado!",
          "Não foi possível buscar os dados. Por favor, recarregue a página ou tente fazer o login novamente.",
          "error"
        );
        console.error(error);
      })
      .finally(() => setLoading(false));
  }

  function getCurrentPagination() {
    const params = queryString.parse(props.location.search);
    const page = (params.page && Number(params.page)) || 1;
    const per = (params.per && Number(params.per)) || 10;

    return { page, per };
  }

  function handleFiltersChange(name, value) {
    if (name === "start_at" && !value) {
      return setFilters((current) => ({
        ...current,
        start_at: null,
        end_at: null,
      }));
    }

    setFilters((current) => ({ ...current, [name]: value }));
  }

  function handleFiltersClean(event) {
    event.preventDefault();
    setFilters(initialState);
  }

  function handleColumnClick(column) {
    const strategyName = stringFields.includes(column)
      ? "stringStrategy"
      : numberFields.includes(column)
      ? "numberStrategy"
      : dateFields.includes(column)
      ? "dateStrategy"
      : "unknownStrategy";

    const strategy = strategiesMap[strategyName];

    const sorted = cashbacks
      .map((v) => v)
      .sort((a, b) => strategy(a, b, column));

    if (isColumnSorted[column]) {
      setIsColumnSorted(columnsSortInitialState);
      return setCashbacks(sorted.reverse());
    }

    setIsColumnSorted({ ...columnsSortInitialState, [column]: true });
    setCashbacks(sorted);
  }


  useEffect(() => {
    const noCpfProvided = !filters.cpf;
    const validCpf = filters.cpf && isCpfValid(filters.cpf);

    if (noCpfProvided || validCpf) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchData();
      }, 500);
    }
  }, [filters, pagination]);

  useEffect(() => {
    const { page, per } = getCurrentPagination();

    if (page !== pagination.page || per !== pagination.per) {
      setPagination({ per, page });
    }
  });

  return (
    <Page>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Page.Title>Listagem de Cashbacks</Page.Title>

      <Filters
        filters={filters}
        onChange={handleFiltersChange}
        onClean={handleFiltersClean}
      />

      <Panel className="panel-list">
        <div className="cashback-options">
          <p>
            Exibindo {cashbacks.length} de {totalCashbacks} cashbacks
          </p>
          {Access.hasPermissions(["manage-cashback-imports"]) && (
            <Link
              to="/pro/cashback/create"
              className="button-add margin-bottom-20"
            >
              Adicionar cashback
            </Link>
          )}
        </div>

        <>
          <table className="table table-curved">
            <thead>
              <tr>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("customer_name")}
                  >
                    Cliente <FaSort />
                  </Button>
                </th>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("customer_cpf")}
                  >
                    CPF <FaSort />
                  </Button>
                </th>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("amount")}
                  >
                    Cashback <FaSort />
                  </Button>
                </th>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("created_at")}
                  >
                    Data de importação <FaSort />
                  </Button>
                </th>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("reference_date")}
                  >
                    Mês de referência <FaSort />
                  </Button>
                </th>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("status")}
                  >
                    Situação <FaSort />
                  </Button>
                </th>
                <th>
                  <Button
                    className="table-header-button"
                    type="button"
                    onClick={() => handleColumnClick("accessor_name")}
                  >
                    Assessor <FaSort />
                  </Button>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <Loading type="page" size="large" />
              ) : !cashbacks.length ? (
                <tr>
                  <td className="text-center" colSpan="10">
                    Nenhum cashback encontrado.
                  </td>
                </tr>
              ) : (
                cashbacks.map((cashback) => (
                  <tr key={cashback.id}>
                    <td>{cashback.customer_name}</td>
                    <td>{cashback.customer_cpf}</td>
                    <td>{cashback.amount}</td>
                    <td>
                      {cashback.created_at &&
                        moment(cashback.created_at).format(
                          "DD/MM/YYYY [às] HH:mm"
                        )}
                    </td>
                    <td>
                      {cashback.reference_date &&
                        moment(cashback.reference_date).format("MM/YYYY")}
                    </td>
                    <td>{parseCashbackImportStatus(cashback.status)}</td>
                    <td>{cashback.accessor_name}</td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <th colSpan="8" />
              </tr>
            </tfoot>
          </table>

          <Pagination
            current={pagination.page}
            per={pagination.per}
            total={totalCashbacks}
            {...props}
          />
        </>
      </Panel>
    </Page>
  );
}
